import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { GoogleMap, useJsApiLoader, MarkerF } from '@react-google-maps/api';
import { getGoogleMapsLoaderOptions } from './googleMapsLoaderConfig';
import CanvasHeatmapOverlay from './CanvasHeatmapOverlay';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale,
  BarElement, Title, Tooltip, Legend
} from 'chart.js';
import {
  FiMap, FiMapPin, FiUser, FiUsers, FiPhone, FiDownload,
  FiMaximize2, FiMinimize2, FiSearch, FiX, FiBarChart2,
  FiAlertTriangle, FiGift, FiHome, FiPrinter, FiFileText,
  FiThermometer, FiList
} from 'react-icons/fi';
import * as XLSX from 'xlsx';
import api from '../../services/api';
import './MapaCriancas.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const CENTRO_AREIOPOLIS = { lat: -22.6607, lng: -48.6606 };
const ZOOM_INICIAL = 14;

const CORES = {
  crianca: '#71717a',
  jovem:   '#8b5cf6',
  adulto:  '#f59e0b',
};

const MESES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho',
               'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

const CATEGORIAS = [
  { valor: 'todos',   label: 'Todos'    },
  { valor: 'crianca', label: 'Crianças' },
  { valor: 'jovem',   label: 'Jovens'   },
  { valor: 'adulto',  label: 'Adultos'  },
];

// ── Helpers ──────────────────────────────────────────────────────────────────
function extrairRua(endereco) {
  if (!endereco) return null;
  const limpo = endereco.replace(/, Areiópolis.*$/i, '').trim();
  const match = limpo.match(/^([^,\-–]+)/);
  return match ? match[1].trim() : limpo;
}

function calcularFaixaEtaria(idade) {
  if (idade == null) return null;
  if (idade <= 5)  return '0–5 anos';
  if (idade <= 11) return '6–11 anos';
  if (idade <= 17) return '12–17 anos';
  if (idade <= 25) return '18–25 anos';
  if (idade <= 35) return '26–35 anos';
  return '36+ anos';
}

const FAIXAS_ORDEM = ['0–5 anos','6–11 anos','12–17 anos','18–25 anos','26–35 anos','36+ anos'];
const FAIXAS_CORES  = ['#93c5fd','#6ee7b7','#fde68a','#c4b5fd','#fca5a5','#fb923c'];

// ── Componente principal ─────────────────────────────────────────────────────
const MapaCriancas = () => {
  const { isLoaded, loadError } = useJsApiLoader(
    getGoogleMapsLoaderOptions(process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '')
  );

  const [membros, setMembros]               = useState([]);
  const [loading, setLoading]               = useState(true);
  const [erro, setErro]                     = useState(null);
  const [modoMapa, setModoMapa]             = useState('pontos');
  const [filtroCategoria, setFiltro]        = useState('todos');
  const [busca, setBusca]                   = useState('');
  const [estatisticas, setEstatisticas]     = useState(null);
  const [membroSel, setMembroSel]           = useState(null);
  const [mostrarSemEnd, setMostrarSemEnd]   = useState(false);
  const [telaCheia, setTelaCheia]           = useState(false);
  const [mostrarAnalise, setMostrarAnalise] = useState(false);
  const [mostrarExport, setMostrarExport]   = useState(false);
  const [mapInstance, setMapInstance]       = useState(null);
  const [geocodLote, setGeocodLote]         = useState(false);
  const [msgGeo, setMsgGeo]                 = useState(null);
  const [gerandoPDF, setGerandoPDF]         = useState(false);

  const containerRef  = useRef(null);
  const exportMenuRef = useRef(null);

  // ── Fechar menu export ao clicar fora ───────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target))
        setMostrarExport(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Monitorar saída de tela cheia (ESC) ─────────────────────────────────────
  useEffect(() => {
    const onFsChange = () => { if (!document.fullscreenElement) setTelaCheia(false); };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // ── Carregar dados ──────────────────────────────────────────────────────────
  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      setErro(null);
      const response = await api.get('/irmandade/mapa');
      const dados = response.data;
      setMembros(dados.membros || []);
      setEstatisticas({
        total:          dados.total,
        comCoordenadas: dados.com_coordenadas,
        semCoordenadas: dados.sem_coordenadas,
      });
    } catch {
      setErro('Erro ao carregar dados. Verifique se o servidor está rodando.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregarDados(); }, [carregarDados]);

  // ── Geocodificar pendentes (lote) ────────────────────────────────────────────
  const geocodificarPendentes = async () => {
    setGeocodLote(true);
    setMsgGeo(null);
    try {
      const res = await api.post('/irmandade/geocodificar-pendentes');
      const d = res.data;
      if (d.total === 0) {
        setMsgGeo({ tipo: 'ok', texto: 'Todos os membros já têm localização.' });
      } else {
        setMsgGeo({ tipo: 'ok', texto: `Geocodificando ${d.total} membro(s) via Google Maps. Recarregue em alguns segundos.` });
        setTimeout(carregarDados, 8000);
      }
    } catch {
      setMsgGeo({ tipo: 'erro', texto: 'Erro ao iniciar geocodificação.' });
    } finally {
      setGeocodLote(false);
    }
  };

  // ── Filtros ──────────────────────────────────────────────────────────────────
  const membrosFiltrados = useMemo(() =>
    membros.filter(m => {
      if (filtroCategoria !== 'todos' && m.categoria !== filtroCategoria) return false;
      if (busca && !m.nome.toLowerCase().includes(busca.toLowerCase())) return false;
      return true;
    }),
  [membros, filtroCategoria, busca]);

  const membrosComCoords  = useMemo(() => membrosFiltrados.filter(m => m.lat && m.lng), [membrosFiltrados]);
  const membrosSemCoords  = useMemo(() => membrosFiltrados.filter(m => !m.lat || !m.lng), [membrosFiltrados]);

  const mapaPronto = isLoaded && !!window.google?.maps;

  const heatmapData = useMemo(
    () => membrosComCoords.map((membro) => ({
      lat: membro.lat,
      lng: membro.lng,
      weight: 1,
    })),
    [membrosComCoords]
  );

  // ── Ícone do marcador ────────────────────────────────────────────────────────
  const getIcon = useCallback((categoria, selecionado) => {
    if (!window.google) return undefined;
    return {
      path:        window.google.maps.SymbolPath.CIRCLE,
      scale:       selecionado ? 12 : 8,
      fillColor:   CORES[categoria] || '#6b7280',
      fillOpacity: 0.85,
      strokeColor: selecionado ? '#18181b' : '#ffffff',
      strokeWeight: selecionado ? 3 : 1.5,
    };
  }, []);

  // ── Tela cheia ───────────────────────────────────────────────────────────────
  const toggleTelaCheia = () => {
    const el = containerRef.current;
    if (!telaCheia) {
      if (el.requestFullscreen) el.requestFullscreen();
      setTelaCheia(true);
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
      setTelaCheia(false);
    }
    setTimeout(() => {
      if (mapInstance && window.google)
        window.google.maps.event.trigger(mapInstance, 'resize');
    }, 350);
  };

  // ── Exportar mapa (print) ────────────────────────────────────────────────────
  const exportarMapaImagem = () => {
    setMostrarExport(false);
    document.body.classList.add('imprimir-mapa');
    window.print();
    setTimeout(() => document.body.classList.remove('imprimir-mapa'), 500);
  };

  // ── Relatório PDF ─────────────────────────────────────────────────────────────
  const baixarRelatorio = async () => {
    setMostrarExport(false);
    setGerandoPDF(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(
        `${window.location.protocol}//${window.location.hostname}:3001/api/irmandade/relatorio-pdf`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) throw new Error('Erro ao gerar PDF');
      const blob = await resp.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `relatorio-territorializacao-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Erro ao gerar relatório PDF. Tente novamente.');
    } finally {
      setGerandoPDF(false);
    }
  };

  // ── Exportar Excel ────────────────────────────────────────────────────────────
  const exportarListaExcel = (apenasSemEndereco = false) => {
    setMostrarExport(false);
    const lista  = apenasSemEndereco ? membrosSemCoords : membrosFiltrados;
    const titulo = apenasSemEndereco ? 'Sem_Localização' : 'Lista_Filtrada';

    const nomeCat = { crianca: 'Criança', jovem: 'Jovem', adulto: 'Adulto' };
    const dados = lista.map(m => ({
      'Nome':               m.nome,
      'Categoria':          nomeCat[m.categoria] || m.categoria,
      'Idade':              m.idade ?? '',
      'Endereço':           m.endereco || '',
      'Localização no Mapa': m.lat ? 'Sim' : 'Não',
      'Responsável':        m.responsavel || '',
      'Telefone':           m.responsavel_telefone || m.telefone || '',
    }));

    const ws = XLSX.utils.json_to_sheet(dados);
    ws['!cols'] = [{ wch: 35 }, { wch: 10 }, { wch: 6 }, { wch: 50 }, { wch: 18 }, { wch: 30 }, { wch: 16 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, titulo);
    XLSX.writeFile(wb, `territorializacao_${titulo}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // ── Análise: Top ruas ─────────────────────────────────────────────────────────
  const topRuas = useMemo(() => {
    const contagem = {};
    membros.forEach(m => {
      const rua = extrairRua(m.endereco);
      if (rua) contagem[rua] = (contagem[rua] || 0) + 1;
    });
    return Object.entries(contagem)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([rua, total]) => ({ rua, total }));
  }, [membros]);

  // ── Análise: Faixas etárias ───────────────────────────────────────────────────
  const faixasEtarias = useMemo(() => {
    const faixas = Object.fromEntries(FAIXAS_ORDEM.map(k => [k, 0]));
    membros.forEach(m => {
      const f = calcularFaixaEtaria(m.idade);
      if (f && faixas[f] !== undefined) faixas[f]++;
    });
    return faixas;
  }, [membros]);

  const chartFaixas = {
    labels: FAIXAS_ORDEM,
    datasets: [{
      label: 'Membros',
      data: FAIXAS_ORDEM.map(k => faixasEtarias[k]),
      backgroundColor: FAIXAS_CORES,
      borderRadius: 6,
      borderSkipped: false,
    }],
  };

  const chartOpcoes = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx) => ` ${ctx.raw} pessoa${ctx.raw !== 1 ? 's' : ''}` } },
    },
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0, font: { size: 11 } }, grid: { color: '#f1f5f9' } },
      x: { ticks: { font: { size: 10 } }, grid: { display: false } },
    },
  };

  // ── Aniversariantes do mês ────────────────────────────────────────────────────
  const mesAtual = new Date().getMonth();
  const aniversariantesMes = useMemo(() =>
    membros
      .filter(m => {
        const nasc = m.data_nascimento || m.dataNascimento;
        if (!nasc) return false;
        return new Date(nasc).getUTCMonth() === mesAtual;
      })
      .sort((a, b) => {
        const da = new Date(a.data_nascimento || a.dataNascimento).getUTCDate();
        const db = new Date(b.data_nascimento || b.dataNascimento).getUTCDate();
        return da - db;
      }),
  [membros, mesAtual]);

  // ── Click na lista: pan para o membro ────────────────────────────────────────
  const handleListClick = (membro) => {
    setMembroSel(membro);
    if (mapInstance && membro.lat && membro.lng) {
      mapInstance.panTo({ lat: membro.lat, lng: membro.lng });
    }
  };

  // ── Estados de carregamento / erro ───────────────────────────────────────────
  if (loading || !isLoaded) {
    return (
      <div className="mapa-loading">
        <div className="mapa-loading-spinner"></div>
        <p>{!isLoaded ? 'Carregando Google Maps…' : 'Carregando dados…'}</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="mapa-erro">
        <FiAlertTriangle size={36} color="#ef4444" />
        <p>Erro ao carregar Google Maps: {loadError.message}</p>
        <p style={{ fontSize: 13, color: '#6b7280' }}>Verifique a chave REACT_APP_GOOGLE_MAPS_API_KEY</p>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="mapa-erro">
        <FiAlertTriangle size={36} color="#f59e0b" />
        <p>{erro}</p>
        <button onClick={carregarDados} className="mapa-btn-recarregar">Tentar novamente</button>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className={`mapa-container${telaCheia ? ' tela-cheia' : ''}`} ref={containerRef}>

      {/* ── Cabeçalho ─────────────────────────────────────────────────────── */}
      <div className="mapa-header">
        <div className="mapa-header-titulo">
          <h1><FiMap className="mapa-titulo-icon" /> Territorialização — Irmandade</h1>
          <p className="mapa-subtitulo">Distribuição geográfica em Areiópolis - SP · Google Maps</p>
        </div>

        {estatisticas && (
          <div className="mapa-stats-bar">
            <div className="mapa-stat">
              <span className="mapa-stat-valor">{estatisticas.total}</span>
              <span className="mapa-stat-label">Total</span>
            </div>
            <div className="mapa-stat mapa-stat-ok">
              <span className="mapa-stat-valor">{estatisticas.comCoordenadas}</span>
              <span className="mapa-stat-label">No mapa</span>
            </div>
            <div className="mapa-stat mapa-stat-warn">
              <span className="mapa-stat-valor">{estatisticas.semCoordenadas}</span>
              <span className="mapa-stat-label">Sem local</span>
            </div>
            <div className="mapa-stat">
              <span className="mapa-stat-valor">
                {estatisticas.total > 0
                  ? Math.round(estatisticas.comCoordenadas / estatisticas.total * 100)
                  : 0}%
              </span>
              <span className="mapa-stat-label">Cobertura</span>
            </div>
            {aniversariantesMes.length > 0 && (
              <div className="mapa-stat mapa-stat-aniv" title={`${aniversariantesMes.length} aniversariante(s) em ${MESES[mesAtual]}`}>
                <span className="mapa-stat-valor"><FiGift size={14} /> {aniversariantesMes.length}</span>
                <span className="mapa-stat-label">Aniv. mês</span>
              </div>
            )}
          </div>
        )}

        <div className="mapa-acoes-header">
          {/* Exportar */}
          <div className="mapa-export-wrapper" ref={exportMenuRef}>
            <button className="mapa-btn-acao" onClick={() => setMostrarExport(!mostrarExport)} title="Exportar">
              <FiDownload size={14} /> Exportar
            </button>
            {mostrarExport && (
              <div className="mapa-export-menu">
                <button onClick={baixarRelatorio} disabled={gerandoPDF} style={gerandoPDF ? { opacity: 0.6, cursor: 'wait' } : {}}>
                  <FiFileText size={15} /> {gerandoPDF ? 'Gerando PDF…' : 'Relatório PDF (A4)'}
                </button>
                <button onClick={exportarMapaImagem}>
                  <FiPrinter size={15} /> Imprimir / Salvar mapa
                </button>
                <button onClick={() => exportarListaExcel(false)}>
                  <FiBarChart2 size={15} /> Lista filtrada (.xlsx)
                </button>
                <button onClick={() => exportarListaExcel(true)} disabled={membrosSemCoords.length === 0}>
                  <FiAlertTriangle size={15} /> Sem localização ({membrosSemCoords.length})
                </button>
              </div>
            )}
          </div>

          {/* Análise */}
          <button
            className={`mapa-btn-acao${mostrarAnalise ? ' ativo' : ''}`}
            onClick={() => setMostrarAnalise(!mostrarAnalise)}
            title="Painel de análise"
          >
            <FiBarChart2 size={14} /> Análise
          </button>

          {/* Geocodificar pendentes */}
          {estatisticas && estatisticas.semCoordenadas > 0 && (
            <button
              className={`mapa-btn-acao mapa-btn-geocodificar${geocodLote ? ' loading' : ''}`}
              onClick={geocodificarPendentes}
              disabled={geocodLote}
              title={`${estatisticas.semCoordenadas} membro(s) sem localização — geocodificar via Google Maps`}
            >
              <FiMapPin size={14} />
              {geocodLote ? 'Buscando…' : `Localizar ${estatisticas.semCoordenadas} pendente${estatisticas.semCoordenadas !== 1 ? 's' : ''}`}
            </button>
          )}

          {/* Tela cheia */}
          <button className="mapa-btn-acao" onClick={toggleTelaCheia} title={telaCheia ? 'Sair da tela cheia (ESC)' : 'Tela cheia'}>
            {telaCheia ? <><FiMinimize2 size={14} /> Sair</> : <><FiMaximize2 size={14} /> Tela cheia</>}
          </button>
        </div>
      </div>

      {/* Mensagem geocodificação em lote */}
      {msgGeo && (
        <div className={`mapa-msg-geolote mapa-msg-geolote-${msgGeo.tipo}`}>
          {msgGeo.tipo === 'ok' ? '✅' : '⚠️'} {msgGeo.texto}
          <button className="mapa-msg-fechar" onClick={() => setMsgGeo(null)}>✕</button>
        </div>
      )}

      {/* ── Controles / Filtros ────────────────────────────────────────────── */}
      <div className="mapa-controles">
        <div className="mapa-controles-linha">
          {/* Modo */}
          <div className="mapa-toggle-grupo">
            <span className="mapa-label">Modo:</span>
            <div className="mapa-toggle">
              <button className={`mapa-toggle-btn ${modoMapa === 'pontos' ? 'ativo' : ''}`} onClick={() => setModoMapa('pontos')}>
                <FiMapPin size={12} /> Pontos
              </button>
              <button
                className={`mapa-toggle-btn ${modoMapa === 'calor' ? 'ativo' : ''}`}
                onClick={() => setModoMapa('calor')}
                disabled={!mapaPronto}
                title={!mapaPronto ? 'Aguarde o mapa carregar' : undefined}
              >
                <FiThermometer size={12} /> Calor
              </button>
            </div>
          </div>

          {/* Categoria */}
          <div className="mapa-toggle-grupo">
            <span className="mapa-label">Categoria:</span>
            <div className="mapa-toggle">
              {CATEGORIAS.map(cat => (
                <button
                  key={cat.valor}
                  className={`mapa-toggle-btn ${cat.valor !== 'todos' ? cat.valor : ''} ${filtroCategoria === cat.valor ? 'ativo' : ''}`}
                  onClick={() => setFiltro(cat.valor)}
                >
                  <FiUsers size={12} /> {cat.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mapa-controles-linha">
          {/* Busca */}
          <div className="mapa-busca-grupo">
            <FiSearch size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
            <input
              type="text"
              className="mapa-busca"
              placeholder="Buscar por nome..."
              value={busca}
              onChange={e => setBusca(e.target.value)}
              style={{ paddingLeft: 32 }}
            />
            {busca && <button className="mapa-busca-limpar" onClick={() => setBusca('')}><FiX size={14} /></button>}
          </div>

          {/* Contagem */}
          <div className="mapa-contagem">
            <strong>{membrosComCoords.length}</strong> no mapa
            {membrosSemCoords.length > 0 && (
              <button className="mapa-btn-sem-endereco" onClick={() => setMostrarSemEnd(!mostrarSemEnd)}>
                + {membrosSemCoords.length} sem localização
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Painel de Análise ─────────────────────────────────────────────── */}
      {mostrarAnalise && (
        <div className="mapa-analise-painel">
          {/* Faixas etárias */}
          <div className="analise-card">
            <h3 className="analise-titulo"><FiBarChart2 size={15} /> Distribuição por Faixa Etária</h3>
            <div className="analise-chart-wrapper">
              <Bar data={chartFaixas} options={chartOpcoes} />
            </div>
          </div>

          {/* Top ruas */}
          <div className="analise-card">
            <h3 className="analise-titulo"><FiHome size={15} /> Ruas com Mais Membros</h3>
            <div className="analise-ruas">
              {topRuas.map((r, i) => (
                <div key={i} className="analise-rua-item">
                  <div className="analise-rua-barra-wrap">
                    <div className="analise-rua-barra" style={{ width: `${(r.total / topRuas[0].total) * 100}%` }}></div>
                  </div>
                  <span className="analise-rua-nome" title={r.rua}>{r.rua.substring(0, 28)}{r.rua.length > 28 ? '…' : ''}</span>
                  <span className="analise-rua-total">{r.total}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Aniversariantes do mês */}
          <div className="analise-card analise-card-aniv">
            <h3 className="analise-titulo"><FiGift size={15} /> Aniversariantes — {MESES[mesAtual]}</h3>
            {aniversariantesMes.length === 0 ? (
              <p className="analise-vazio">Nenhum aniversariante neste mês</p>
            ) : (
              <div className="analise-aniv-lista">
                {aniversariantesMes.map((m, i) => {
                  const dia = new Date(m.data_nascimento || m.dataNascimento).getUTCDate();
                  return (
                    <div key={i} className="analise-aniv-item">
                      <span className="analise-aniv-dia">dia {dia}</span>
                      <span className="analise-aniv-nome">{m.nome}</span>
                      <span className="analise-aniv-idade">{m.idade} anos</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Cobertura por categoria */}
          <div className="analise-card">
            <h3 className="analise-titulo"><FiMapPin size={15} /> Cobertura no Mapa</h3>
            <div className="analise-cobertura">
              {['crianca', 'jovem', 'adulto'].map(cat => {
                const total    = membros.filter(m => m.categoria === cat).length;
                const comCoord = membros.filter(m => m.categoria === cat && m.lat).length;
                const pct = total > 0 ? Math.round(comCoord / total * 100) : 0;
                const labels = { crianca: 'Crianças', jovem: 'Jovens', adulto: 'Adultos' };
                return (
                  <div key={cat} className="cobertura-item">
                    <div className="cobertura-titulo">
                      <span><FiUser size={12} /> {labels[cat]}</span>
                      <span className="cobertura-pct">{pct}%</span>
                    </div>
                    <div className="cobertura-barra-bg">
                      <div className={`cobertura-barra cobertura-barra-${cat}`} style={{ width: `${pct}%` }}></div>
                    </div>
                    <div className="cobertura-detalhe">{comCoord} de {total} com localização</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Mapa + Lista lateral ────────────────────────────────────────────── */}
      <div className="mapa-layout" id="mapa-area-impressao">
        {/* Mapa Google */}
        <div className="mapa-wrapper">
          <GoogleMap
            mapContainerClassName="mapa-leaflet"
            center={CENTRO_AREIOPOLIS}
            zoom={ZOOM_INICIAL}
            onLoad={setMapInstance}
            options={{
              fullscreenControl:  false,
              streetViewControl:  false,
              mapTypeControl:     false,
              zoomControl:        true,
              gestureHandling:    'cooperative',
              mapTypeId:          'roadmap',
            }}
          >
            {/* Marcadores em modo pontos */}
            {modoMapa === 'pontos' && membrosComCoords.map(membro => (
              <MarkerF
                key={membro.id}
                position={{ lat: membro.lat, lng: membro.lng }}
                icon={getIcon(membro.categoria, membroSel?.id === membro.id)}
                zIndex={membroSel?.id === membro.id ? 100 : 1}
                onClick={() => setMembroSel(membro)}
                title={membro.nome}
              />
            ))}

            {modoMapa === 'calor' && (
              <CanvasHeatmapOverlay
                points={heatmapData}
                radius={35}
                opacity={0.7}
                gradient={['rgba(139,92,246,0)', 'rgba(139,92,246,.35)', 'rgba(139,92,246,.7)', 'rgba(91,33,182,.95)']}
              />
            )}
          </GoogleMap>

          {/* Popup customizado do membro selecionado (sem InfoWindow do Google) */}
          {membroSel && (
            <div className="mapa-popup-custom">
              <button
                className="popup-fechar"
                onClick={() => setMembroSel(null)}
                title="Fechar"
                type="button"
              >✕</button>
              <div className={`popup-categoria popup-cat-${membroSel.categoria}`}>
                {membroSel.categoria === 'crianca' ? '👶 Criança'
                 : membroSel.categoria === 'jovem'  ? '🧒 Jovem'
                 : '🙏 Adulto'}
                {membroSel.idade != null && ` · ${membroSel.idade} anos`}
              </div>
              <h3 className="popup-nome">{membroSel.nome}</h3>
              {membroSel.endereco && (
                <p className="popup-endereco"><FiMapPin size={11} /> {membroSel.endereco.replace(', Areiópolis - SP', '')}</p>
              )}
              {membroSel.responsavel && (
                <p className="popup-responsavel"><FiUser size={11} /> {membroSel.responsavel}</p>
              )}
              {(membroSel.responsavel_telefone || membroSel.telefone) && (
                <a
                  href={`https://wa.me/55${(membroSel.responsavel_telefone || membroSel.telefone).replace(/\D/g,'')}`}
                  target="_blank" rel="noreferrer"
                  className="popup-telefone-link"
                >
                  <FiPhone size={12} /> {membroSel.responsavel_telefone || membroSel.telefone}
                </a>
              )}
            </div>
          )}

          {/* Legenda */}
          <div className="mapa-legenda">
            {modoMapa === 'pontos' ? (
              <>
                <div className="legenda-item"><span className="legenda-cor" style={{ background: CORES.crianca }}></span><span>Crianças</span></div>
                <div className="legenda-item"><span className="legenda-cor" style={{ background: CORES.jovem   }}></span><span>Jovens</span></div>
                <div className="legenda-item"><span className="legenda-cor" style={{ background: CORES.adulto  }}></span><span>Adultos</span></div>
              </>
            ) : (
              <>
                <div className="legenda-item">
                  <div className="legenda-gradiente"></div>
                  <div className="legenda-calor-labels"><span>Menor</span><span>Maior</span></div>
                </div>
                <span className="legenda-hint">concentração</span>
              </>
            )}
          </div>

          {/* Botão tela cheia sobre o mapa */}
          <button className="mapa-btn-fullscreen-overlay" onClick={toggleTelaCheia} title={telaCheia ? 'Sair da tela cheia (ESC)' : 'Tela cheia'}>
            {telaCheia ? <FiMinimize2 size={16} /> : <FiMaximize2 size={16} />}
          </button>
        </div>

        {/* Lista lateral */}
        <div className="mapa-lista">
          <div className="mapa-lista-header">
            <h3><FiList size={14} /> Lista ({membrosFiltrados.length})</h3>
            <button className="mapa-lista-exportar" onClick={() => exportarListaExcel(false)} title="Exportar lista filtrada">
              <FiDownload size={12} /> .xlsx
            </button>
          </div>

          <div className="mapa-lista-scroll">
            {membrosFiltrados.length === 0 ? (
              <div className="mapa-lista-vazia">Nenhum resultado encontrado</div>
            ) : (
              membrosFiltrados.map(membro => (
                <div
                  key={membro.id}
                  className={`mapa-lista-item ${!membro.lat || !membro.lng ? 'sem-coords' : ''} ${membroSel?.id === membro.id ? 'selecionado' : ''}`}
                  onClick={() => handleListClick(membro)}
                >
                  <div className={`lista-categoria-dot lista-cat-${membro.categoria}`}></div>
                  <div className="lista-info">
                    <span className="lista-nome">{membro.nome}</span>
                    <span className="lista-meta">
                      {membro.idade != null ? `${membro.idade} anos` : ''}
                      {membro.endereco
                        ? ` · ${membro.endereco.replace(', Areiópolis - SP', '').substring(0, 30)}`
                        : <span className="lista-sem-endereco"> · Sem endereço</span>
                      }
                    </span>
                  </div>
                  {(!membro.lat || !membro.lng) && (
                    <span className="lista-badge-sem-coords" title="Sem localização no mapa"><FiMapPin size={12} color="#f59e0b" /></span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* ── Painel sem endereço ──────────────────────────────────────────────── */}
      {mostrarSemEnd && membrosSemCoords.length > 0 && (
        <div className="mapa-painel-sem-endereco">
          <div className="painel-sem-header">
            <h3><FiAlertTriangle size={15} /> Sem localização ({membrosSemCoords.length})</h3>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="painel-sem-exportar" onClick={() => exportarListaExcel(true)} title="Exportar como Excel">
                <FiDownload size={13} /> Excel
              </button>
              <button onClick={() => setMostrarSemEnd(false)}><FiX size={16} /></button>
            </div>
          </div>
          <div className="painel-sem-lista">
            {membrosSemCoords.map(m => (
              <div key={m.id} className="painel-sem-item">
                <span className={`painel-sem-cat painel-cat-${m.categoria}`}><FiUser size={13} /></span>
                <span className="painel-sem-nome">{m.nome}</span>
                <span className="painel-sem-idade">{m.idade != null ? `${m.idade}a` : ''}</span>
                <span className="painel-sem-endereco">{m.endereco || 'Sem endereço cadastrado'}</span>
                {(m.responsavel_telefone || m.telefone) && (
                  <a href={`https://wa.me/55${(m.responsavel_telefone || m.telefone).replace(/\D/g,'')}`} target="_blank" rel="noreferrer" className="painel-sem-tel">
                    <FiPhone size={12} /> {m.responsavel_telefone || m.telefone}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MapaCriancas;
